if (!customElements.get("persuva-addons")) {
	// Constants for better maintainability
	const CONTEXT = {
		PRODUCT_INFO: "product-info",
		CART_DRAWER: "cart-drawer",
	};

	const EVENTS = {
		CART_UPDATE: "cart:updated",
		CART_REFRESH: "cart:refresh",
		STORAGE_CHANGE: "storage",
		PROTECTION_STORAGE_CHANGE: "protection:storage-change",
	};

	class PersuvaAddons extends HTMLElement {
		constructor() {
			super();
			this.checkbox = this.querySelector(".shipping-protection__checkbox");
			// Convert to number to match cart API response type
			this.variantId = parseInt(this.dataset.variantId, 10);
			this.productId = this.dataset.productId;
			this.context = this.dataset.context || CONTEXT.CART_DRAWER;

			this.isProcessing = false;
			this.currentCartState = this.dataset.inCart === "true";

			// Bind methods
			this.boundHandleToggle = this.handleToggle.bind(this);
			this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
			this.boundHandleStorageChange = this.handleStorageChange.bind(this);
			this.boundHandleProtectionStorageChange =
				this.handleProtectionStorageChange.bind(this);

			this.init();
		}

		init() {
			if (!this.checkbox || !this.variantId) {
				return;
			}

			// Setup checkbox listener
			this.checkbox.addEventListener("change", this.boundHandleToggle);

			// Setup storage sync
			this.storageKey = `protection-state-${this.variantId}`;
			window.addEventListener("storage", this.boundHandleStorageChange);
			document.addEventListener(
				EVENTS.PROTECTION_STORAGE_CHANGE,
				this.boundHandleProtectionStorageChange,
			);

			// Setup cart update listeners
			document.addEventListener(EVENTS.CART_UPDATE, this.boundHandleCartUpdate);
			document.addEventListener(
				EVENTS.CART_REFRESH,
				this.boundHandleCartUpdate,
			);

			if (
				typeof PUB_SUB_EVENTS !== "undefined" &&
				typeof subscribe !== "undefined"
			) {
				this.cartUpdateUnsubscriber = subscribe(
					PUB_SUB_EVENTS.cartUpdate,
					(event) => {
						if (event.source !== "protection-addon") {
							this.handleCartUpdate();
						}
					},
				);
			}

			// Initialize state
			this.currentCartState = this.dataset.inCart === "true";

			this.syncWithLocalStorage();
			this.handleCartUpdate(); // Initial sync with cart state

			// Product-page add-ons are submitted by product-form in the main cart/add payload.
		}

		connectedCallback() {
			this.setAttribute("role", "region");
			this.setAttribute("aria-label", "Product Add-ons");
		}

		disconnectedCallback() {
			document.removeEventListener(
				EVENTS.CART_UPDATE,
				this.boundHandleCartUpdate,
			);
			document.removeEventListener(
				EVENTS.CART_REFRESH,
				this.boundHandleCartUpdate,
			);
			window.removeEventListener("storage", this.boundHandleStorageChange);
			document.removeEventListener(
				EVENTS.PROTECTION_STORAGE_CHANGE,
				this.boundHandleProtectionStorageChange,
			);

			if (this.cartUpdateUnsubscriber) {
				this.cartUpdateUnsubscriber();
			}
		}

		async handleToggle(event) {
			// Prevent multiple simultaneous operations
			if (this.isProcessing) {
				event.preventDefault();
				this.checkbox.checked = this.currentCartState;
				return;
			}

			const shouldAdd = event.target.checked;

			// In product-info context, just update state - actual cart add happens with main product
			if (this.context === CONTEXT.PRODUCT_INFO) {
				this.currentCartState = shouldAdd;
				this.updateLocalStorage(shouldAdd);
				return;
			}

			// In cart-drawer context, directly add/remove from cart
			this.currentCartState = shouldAdd;
			this.updateLocalStorage(shouldAdd);

			try {
				this.setLoading(true);

				if (shouldAdd) {
					await this.addToCart();
				} else {
					await this.removeFromCart();
				}

				await this.refreshCartUI();

				// Dispatch update event
				document.dispatchEvent(
					new CustomEvent(EVENTS.CART_UPDATE, {
						bubbles: true,
						detail: {
							variantId: this.variantId,
							inCart: shouldAdd,
						},
					}),
				);
			} catch (error) {
				console.error("[PersuvaAddons] Error updating cart:", error);
				// Revert on error
				this.checkbox.checked = !shouldAdd;
				this.currentCartState = !shouldAdd;
				this.updateLocalStorage(!shouldAdd);
				this.showError(error.message || "Failed to update protection");
			} finally {
				this.setLoading(false);
			}
		}

		async addToCart() {
			const currentCart = await this.getCurrentCart();

			const existingItem = currentCart.items.find(
				(item) => item.variant_id === this.variantId,
			);

			if (existingItem) {
				return;
			}

			const response = await fetch("/cart/add.js", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					id: String(this.variantId),
					quantity: 1,
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				console.error(
					"Add to cart failed:",
					error,
					"Variant ID:",
					this.variantId,
				);
				throw new Error(
					error.message || error.description || "Failed to add protection",
				);
			}

			await response.json();
			this.currentCartState = true;
			this.dataset.inCart = "true";
		}

		async removeFromCart() {
			const currentCart = await this.getCurrentCart();
			const existingItem = currentCart.items.find(
				(item) => item.variant_id === this.variantId,
			);

			if (!existingItem) {
				this.currentCartState = false;
				return;
			}

			const response = await fetch("/cart/change.js", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					id: String(this.variantId),
					quantity: 0,
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				console.error(
					"Remove from cart failed:",
					error,
					"Variant ID:",
					this.variantId,
				);
				throw new Error(
					error.message || error.description || "Failed to remove protection",
				);
			}

			await response.json();
			this.currentCartState = false;
			this.dataset.inCart = "false";
		}

		async getCurrentCart() {
			const response = await fetch("/cart.js");
			if (!response.ok) {
				throw new Error("Failed to fetch cart");
			}
			return await response.json();
		}

		async handleCartUpdate() {
			if (this.isProcessing) return;

			try {
				const cart = await this.getCurrentCart();
				const isInCart = cart.items.some(
					(item) => item.variant_id === this.variantId,
				);
				this.dataset.inCart = isInCart ? "true" : "false";

				if (this.currentCartState !== isInCart) {
					this.currentCartState = isInCart;

					if (this.checkbox) {
						this.checkbox.checked = isInCart;
					}

					this.updateLocalStorage(isInCart);
				}
			} catch (error) {
				console.error("Error syncing with cart:", error);
			}
		}

		/**
		 * Handle cross-tab storage changes (different browser tabs)
		 */
		handleStorageChange(event) {
			if (this.isProcessing) return;

			// Only sync if the variant ID matches (same add-on product)
			if (event.key === this.storageKey && event.newValue !== null) {
				const shouldBeChecked = event.newValue === "true";
				if (this.checkbox && this.checkbox.checked !== shouldBeChecked) {
					this.checkbox.checked = shouldBeChecked;
					this.currentCartState = shouldBeChecked;
				}
			}
		}

		/**
		 * Handle same-page storage changes (different instances on same page)
		 * This enables bidirectional sync between product-info and cart-drawer
		 */
		handleProtectionStorageChange(event) {
			if (this.isProcessing) return;

			// Only sync if the variant ID matches (same add-on product)
			// This ensures different add-ons remain independent
			if (
				event.detail.variantId === this.variantId &&
				event.detail.key === this.storageKey
			) {
				const shouldBeChecked = event.detail.newValue === "true";
				if (this.checkbox && this.checkbox.checked !== shouldBeChecked) {
					this.checkbox.checked = shouldBeChecked;
					this.currentCartState = shouldBeChecked;
				}
			}
		}

		/**
		 * Update localStorage and dispatch custom event for same-page sync
		 */
		updateLocalStorage(isActive) {
			try {
				localStorage.setItem(this.storageKey, isActive.toString());

				// Dispatch custom event with variant ID for targeted syncing
				document.dispatchEvent(
					new CustomEvent(EVENTS.PROTECTION_STORAGE_CHANGE, {
						detail: {
							key: this.storageKey,
							newValue: isActive.toString(),
							variantId: this.variantId,
							context: this.context,
						},
					}),
				);
			} catch (error) {
				console.warn("Could not update localStorage:", error);
			}
		}

		syncWithLocalStorage() {
			try {
				const storedState = localStorage.getItem(this.storageKey);
				if (storedState !== null) {
					const shouldBeChecked = storedState === "true";
					if (this.checkbox) {
						this.checkbox.checked = shouldBeChecked;
						this.currentCartState = shouldBeChecked;
					}
				}
			} catch (error) {
				console.warn("Could not read from localStorage:", error);
			}
		}

		reinitialize() {
			if (this.checkbox) {
				this.checkbox.removeEventListener("change", this.boundHandleToggle);
			}

			this.checkbox = this.querySelector(".shipping-protection__checkbox");
			this.currentCartState = this.dataset.inCart === "true";

			if (this.checkbox) {
				this.checkbox.addEventListener("change", this.boundHandleToggle);

				this.syncWithLocalStorage();
				this.handleCartUpdate();
			}
		}

		dispatchCartEvents() {
			document.documentElement.dispatchEvent(
				new CustomEvent(EVENTS.CART_UPDATE, {
					bubbles: true,
				}),
			);

			if (
				typeof publish !== "undefined" &&
				typeof PUB_SUB_EVENTS !== "undefined"
			) {
				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: "protection-addon",
					variantId: this.variantId,
				});
			}
		}

		async refreshCartUI() {
			try {
				await this.refreshCartDrawerSections();
			} catch (error) {
				console.error("Protection Addon: Error refreshing cart UI:", error);
			}
		}

		async refreshCartDrawerSections() {
			try {
				const sectionsToRender = [
					{
						id: "CartDrawer",
						section: "cart-drawer",
						selector: ".drawer__inner",
					},
					{
						id: "cart-icon-bubble",
						section: "cart-icon-bubble",
						selector: ".shopify-section",
					},
				];

				const response = await fetch("/cart.js");
				if (!response.ok) {
					this.dispatchCartEvents();
					return;
				}

				const cart = await response.json();

				const sectionsUrl = `${window.location.pathname}?${sectionsToRender.map((s) => `sections[]=${s.section}`).join("&")}`;
				const sectionsResponse = await fetch(sectionsUrl);

				if (!sectionsResponse.ok) {
					this.dispatchCartEvents();
					return;
				}

				const sectionsData = await sectionsResponse.json();

				sectionsToRender.forEach((section) => {
					const element = document.getElementById(section.id);
					if (element && sectionsData[section.section]) {
						const targetElement = section.selector
							? element.querySelector(section.selector) || element
							: element;

						const newHTML = this.getSectionInnerHTML(
							sectionsData[section.section],
							section.selector,
						);
						if (newHTML) {
							targetElement.innerHTML = newHTML;
						}
					}
				});

				const cartDrawer = document.querySelector("cart-drawer");
				if (cartDrawer) {
					cartDrawer.classList.toggle("is-empty", cart.item_count === 0);
				}
			} catch (error) {
				console.error(
					"Protection Addon: Error refreshing cart drawer sections:",
					error,
				);
				this.dispatchCartEvents();
			}
		}

		getSectionInnerHTML(html, selector = ".shopify-section") {
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, "text/html");
			const element = doc.querySelector(selector);
			return element ? element.innerHTML : null;
		}

		setLoading(loading) {
			this.isProcessing = loading;

			if (this.checkbox) {
				this.checkbox.disabled = loading;
			}

			this.classList.toggle("loading", loading);
			this.setAttribute("aria-busy", loading.toString());
		}

		showError(message) {
			const errorElement =
				this.querySelector(".shipping-protection__error") ||
				this.createErrorElement();
			errorElement.textContent = message;
			errorElement.style.display = "block";

			setTimeout(() => {
				errorElement.style.display = "none";
			}, 5000);
		}

		createErrorElement() {
			const errorElement = document.createElement("div");
			errorElement.className = "shipping-protection__error";
			errorElement.setAttribute("role", "alert");
			this.appendChild(errorElement);
			return errorElement;
		}
	}

	customElements.define("persuva-addons", PersuvaAddons);

	// Re-initialize add-ons when cart drawer is updated
	document.addEventListener("DOMContentLoaded", () => {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === "childList") {
					mutation.addedNodes.forEach((node) => {
						if (node.nodeType === Node.ELEMENT_NODE) {
							const protectionComponents = node.querySelectorAll
								? node.querySelectorAll("persuva-addons")
								: [];

							if (node.tagName === "PERSUVA-ADDONS") {
								if (node.reinitialize) node.reinitialize();
							}

							protectionComponents.forEach((component) => {
								if (component.reinitialize) {
									setTimeout(() => component.reinitialize(), 100);
								}
							});
						}
					});
				}
			});
		});

		const cartDrawer = document.querySelector("cart-drawer-items");
		if (cartDrawer) {
			observer.observe(cartDrawer, {
				childList: true,
				subtree: true,
			});
		}
	});
}
